import React, { useState, useEffect } from 'react';
import { useMutation } from '@apollo/client';
import { CREATE_EMPLOYEE_VACATION, UPDATE_EMPLOYEE_VACATION } from '../../graphql/team-availability-mutations';
import { toast } from 'react-hot-toast';

const VACATION_TYPES = ['PTO', 'SICK', 'PERSONAL', 'UNPAID', 'OTHER'];

function VacationFormModal({ isOpen, onClose, vacation, userId: propUserId, users, onSaved }) {
  const [formData, setFormData] = useState({
    userId: propUserId || '',
    startDate: '',
    endDate: '',
    vacationType: '',
    reason: ''
  });

  useEffect(() => {
    if (vacation) {
      const startDateStr = new Date(vacation.startDate).toISOString().split('T')[0];
      const endDateStr = new Date(vacation.endDate).toISOString().split('T')[0];
      setFormData({
        userId: vacation.userId,
        startDate: startDateStr,
        endDate: endDateStr,
        vacationType: vacation.vacationType || '',
        reason: vacation.reason || ''
      });
    } else if (propUserId) {
      setFormData(prev => ({ ...prev, userId: propUserId }));
    } else {
      setFormData({
        userId: '',
        startDate: '',
        endDate: '',
        vacationType: '',
        reason: ''
      });
    }
  }, [vacation, propUserId]);

  const [createVacation, { loading: creating }] = useMutation(CREATE_EMPLOYEE_VACATION, {
    onCompleted: () => {
      toast.success('Vacation created successfully');
      onSaved?.();
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to create vacation');
    }
  });

  const [updateVacation, { loading: updating }] = useMutation(UPDATE_EMPLOYEE_VACATION, {
    onCompleted: () => {
      toast.success('Vacation updated successfully');
      onSaved?.();
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to update vacation');
    }
  });

  const loading = creating || updating;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.userId || !formData.startDate || !formData.endDate) {
      toast.error('Employee, start date, and end date are required');
      return;
    }

    const input = {
      userId: parseInt(formData.userId),
      startDate: new Date(formData.startDate),
      endDate: new Date(formData.endDate),
      vacationType: formData.vacationType || null,
      reason: formData.reason || null
    };

    if (vacation) {
      updateVacation({
        variables: {
          id: vacation.id,
          input
        }
      });
    } else {
      createVacation({
        variables: { input }
      });
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">
              {vacation ? 'Edit Time Off' : 'Add Time Off'}
            </h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500"
            >
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Employee *
              </label>
              <select
                value={formData.userId}
                onChange={(e) => setFormData({ ...formData, userId: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-sky-500 focus:border-sky-500"
                required
                disabled={!!vacation}
              >
                <option value="">Select employee</option>
                {users.map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.name} ({user.role})
                  </option>
                ))}
              </select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Start Date *
                </label>
                <input
                  type="date"
                  value={formData.startDate}
                  onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-sky-500 focus:border-sky-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  End Date *
                </label>
                <input
                  type="date"
                  value={formData.endDate}
                  onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-sky-500 focus:border-sky-500"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Type
              </label>
              <select
                value={formData.vacationType}
                onChange={(e) => setFormData({ ...formData, vacationType: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-sky-500 focus:border-sky-500"
              >
                <option value="">None</option>
                {VACATION_TYPES.map((type) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Reason
              </label>
              <textarea
                value={formData.reason}
                onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-sky-500 focus:border-sky-500"
              />
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-sky-600 hover:bg-sky-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-sky-500 disabled:opacity-50"
              >
                {loading ? 'Saving...' : 'Add/Update'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default VacationFormModal;

